/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/admin/UserCreateModal.tsx
 * Release: v2.0.0 - Modale creazione utenti con email opzionale per admin/accettazione
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { getAssignableOptions, getUserLevel } from '../../utils/hierarchy';
import { CheckCircle, UserPlus, X } from 'lucide-react';

// interface Group { // Non più necessario - usa getAssignableOptions
//   id: string;
//   name: string;
// }

interface CreatedUserData {
  id: string;
  email: string;
  name: string;
  surname: string;
  customerType?: 'company' | 'individual';
}

interface UserCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (userData?: CreatedUserData) => void;
  embedded?: boolean; // Se true, renderizza inline senza overlay modal
}

interface CreateUserData {
  name: string;
  surname: string;
  email: string;
  role: string;
  group_id: string;
  sede_assegnata: 'misterbianco' | 'palermo' | null;
  sendVerificationEmail: boolean;
  completeProfile: boolean;
  customerType: 'azienda' | 'privato';
  password?: string;
  useDirectPassword: boolean;
}

const UserCreateModal: React.FC<UserCreateModalProps> = ({ isOpen, onClose, onSave, embedded = false }) => {
  const [formData, setFormData] = useState<CreateUserData>({
    name: '',
    surname: '',
    email: '',
    role: 'user',
    group_id: 'client',
    sede_assegnata: null,
    sendVerificationEmail: true,
    completeProfile: false,
    customerType: 'azienda',
    password: '',
    useDirectPassword: false
  });
  // const [groups, setGroups] = useState<Group[]>([]); // Non più necessario - usa getAssignableOptions
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getToken, user } = useAuth();

  // Stato per popup successo
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdUser, setCreatedUser] = useState<{ id: string; email: string } | null>(null);

  // Determina se l'utente corrente può omettere la verifica email
  const canSkipEmailVerification = user?.role === 'admin' || user?.role === 'superadmin' || user?.group_id === 'accettazione';

  useEffect(() => {
    if (isOpen) {
      // Reset form
      setFormData({
        name: '',
        surname: '',
        email: '',
        role: 'user',
        group_id: 'client',
        sede_assegnata: null,
        sendVerificationEmail: true,
        completeProfile: false,
        customerType: 'azienda',
        password: '',
        useDirectPassword: false
      });
      setError(null);
      
      // Carica gruppi
      fetchGroups();
    }
  }, [isOpen]);

  const fetchGroups = async () => {
    try {
      // Verifica che l'utente corrente abbia permessi admin
      const currentUser = user;
      const hasAdminPermissions = currentUser && (
        ['superadmin', 'admin', 'accettazione'].includes(currentUser.role) ||
        ['superadmin', 'admin', 'accettazione'].includes(currentUser.group_id)
      );
      
      if (!hasAdminPermissions) {
        console.log('⚠️ [UserCreateModal] fetchGroups: Utente non autorizzato:', { 
          role: currentUser?.role, 
          group_id: currentUser?.group_id 
        });
        return;
      }

      const token = getToken();
      const response = await fetch('/api/groups', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        console.error('❌ [UserCreateModal] fetchGroups: Errore API:', response.status);
        return;
      }
      // const data: Group[] = await response.json(); // Non più necessario - usa getAssignableOptions
      // setGroups(data); // Non più necessario - usa getAssignableOptions
    } catch (error) {
      console.error("❌ [UserCreateModal] Errore caricamento gruppi:", error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      
      // Se si attiva useDirectPassword, disattiva sendVerificationEmail e attiva completeProfile
      if (name === 'useDirectPassword' && checked) {
        setFormData(prev => ({ ...prev, [name]: checked, sendVerificationEmail: false, completeProfile: true }));
      } else if (name === 'sendVerificationEmail' && checked) {
        setFormData(prev => ({ ...prev, [name]: checked, useDirectPassword: false, password: '' }));
      } else {
        setFormData(prev => ({ ...prev, [name]: checked }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Validazione password se è richiesta
    if (formData.useDirectPassword && (!formData.password || formData.password.length < 6)) {
      setError('La password deve essere di almeno 6 caratteri');
      setIsLoading(false);
      return;
    }

    try {
      const token = getToken();
      const response = await fetch('/api/users', {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...formData, customerType: formData.customerType === 'azienda' ? 'company' : 'individual' })
      });

      if (!response.ok) {
        const errorData = await response.json();
        const msg = response.status === 409
          ? (errorData.details || 'Email già registrata nel sistema. Usa un\'email diversa.')
          : (errorData.error || 'Errore nella creazione utente');
        throw new Error(msg);
      }

      const userData = await response.json(); // Consuma la risposta
      const userId = userData.user?.id;

      // Se l'utente vuole completare il profilo, mostra popup successo con opzioni
      if (formData.completeProfile && userId) {
        setCreatedUser({ id: userId, email: formData.email });
        setShowSuccessModal(true);
        // Non chiamare onSave/onClose ancora - aspetta scelta utente
      } else {
        // Creazione senza completamento profilo - chiudi direttamente
        onSave();
        onClose();
      }
    } catch (err: any) {
      setError(err.message || 'Errore durante la creazione');
    } finally {
      setIsLoading(false);
    }
  };

  // Handler per completare profilo dal popup successo
  const handleCompleteProfile = () => {
    // Salva i dati utente prima di resettare lo stato
    const userData = createdUser;
    setShowSuccessModal(false);
    setCreatedUser(null);
    // Passa i dati utente al callback - il parent deciderà se aprire modal o navigare
    if (userData) {
      onSave({
        id: userData.id,
        email: userData.email,
        name: formData.name,
        surname: formData.surname,
        customerType: formData.customerType === 'azienda' ? 'company' : 'individual'
      });
    } else {
      onSave();
    }
    onClose();
  };

  // Handler per chiudere senza completare profilo
  const handleCloseSuccess = () => {
    setShowSuccessModal(false);
    setCreatedUser(null);
    onSave(); // Senza dati = non richiede completamento profilo
    onClose();
  };

  if (!isOpen && !embedded) return null;

  // Contenuto del form (condiviso tra embedded e modal)
  const formContent = (
    <>
      {error && (
        <div className={`${embedded ? 'mb-4' : 'fixed top-4 left-1/2 transform -translate-x-1/2 z-[60]'} bg-red-900 border border-red-700 text-red-200 px-6 py-4 rounded-lg shadow-lg max-w-md`}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1">Nome *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
            placeholder="Nome utente"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1">Cognome</label>
          <input
            type="text"
            name="surname"
            value={formData.surname}
            onChange={handleChange}
            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
            placeholder="Cognome utente"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1">Email *</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
            placeholder="email@esempio.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1">
            Ruolo
            <span className="text-xs text-slate-400 ml-1">
              (Livello max: {getUserLevel(user as any)})
            </span>
          </label>
          <select
            name="role"
            value={formData.role}
            onChange={handleChange}
            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
          >
            {getAssignableOptions(user as any).roles.map(role => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1">
            Gruppo
            <span className="text-xs text-slate-400 ml-1">
              (Solo livelli inferiori al tuo)
            </span>
          </label>
          <select
            name="group_id"
            value={formData.group_id}
            onChange={handleChange}
            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
          >
            {getAssignableOptions(user as any).groups.map(group => (
              <option key={group.value} value={group.value}>
                {group.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1">
            Sede Assegnata
            <span className="text-xs text-slate-400 ml-1">
              (Admin possono commutare)
            </span>
          </label>
          <select
            name="sede_assegnata"
            value={formData.sede_assegnata || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              sede_assegnata: e.target.value === '' ? null : e.target.value as 'misterbianco' | 'palermo'
            }))}
            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
          >
            <option value="">🔄 Nessuna (può commutare - Admin)</option>
            <option value="misterbianco">🔵 Misterbianco</option>
            <option value="palermo">🟠 Palermo</option>
          </select>
          <p className="text-xs text-slate-400 mt-1">
            Imposta una sede fissa per operatori. Lascia vuoto per admin che devono poter commutare.
          </p>
        </div>

        {canSkipEmailVerification && (
          <div className="space-y-3 border-t border-slate-600 pt-4">
            <div className="text-sm text-slate-400 mb-2">
              🔐 <strong>Metodo Autenticazione:</strong>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="sendVerificationEmail"
                checked={formData.sendVerificationEmail}
                onChange={handleChange}
                className="mr-2 text-blue-500 focus:ring-blue-500"
              />
              <label className="text-sm text-slate-300">
                📧 Invia email di verifica (utente imposta password autonomamente)
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="useDirectPassword"
                checked={formData.useDirectPassword}
                onChange={handleChange}
                className="mr-2 text-blue-500 focus:ring-blue-500"
              />
              <label className="text-sm text-slate-300">
                🔑 Imposta password direttamente
              </label>
            </div>

            {formData.useDirectPassword && (
              <div className="ml-6">
                <label className="block text-sm font-medium text-slate-300 mb-1">Password *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password || ''}
                  onChange={handleChange}
                  required={formData.useDirectPassword}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  placeholder="Almeno 6 caratteri"
                  minLength={6}
                />
                <div className="text-xs text-slate-400 mt-1">
                  L'utente potrà loggarsi immediatamente con questa password
                </div>
              </div>
            )}

            {!formData.sendVerificationEmail && !formData.useDirectPassword && (
              <div className="bg-yellow-900 border border-yellow-700 text-yellow-200 px-3 py-2 rounded text-sm">
                ⚠️ Attenzione: L'utente dovrà usare "Recupera Password" per impostare una password
              </div>
            )}

            <div className="border-t border-slate-600 pt-3 mt-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="completeProfile"
                  checked={formData.completeProfile}
                  onChange={handleChange}
                  className="mr-2 text-blue-500 focus:ring-blue-500"
                />
                <label className="text-sm text-slate-300">
                  📋 Completa profilo dopo creazione
                </label>
              </div>
              {/* Tipo cliente — visibile quando "Completa profilo" è attivo */}
              {formData.completeProfile && (
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, customerType: 'azienda' }))}
                    className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                      formData.customerType === 'azienda'
                        ? 'border-blue-500 bg-blue-500/20 text-white'
                        : 'border-slate-600 bg-slate-700/30 text-gray-400'
                    }`}
                  >
                    🏢 Azienda
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, customerType: 'privato' }))}
                    className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                      formData.customerType === 'privato'
                        ? 'border-purple-500 bg-purple-500/20 text-white'
                        : 'border-slate-600 bg-slate-700/30 text-gray-400'
                    }`}
                  >
                    👤 Privato
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-slate-300 bg-slate-600 hover:bg-slate-500 rounded-lg transition-colors disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creazione...
              </>
            ) : (
              'Crea Utente'
            )}
          </button>
        </div>
      </form>
    </>
  );

  // Popup successo stilizzato (definito qui per essere disponibile in entrambe le modalità)
  const successModal = showSuccessModal && createdUser && (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className="bg-slate-800 rounded-2xl border border-slate-700 shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header con gradiente */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-full">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Utente creato con successo!</h3>
              <p className="text-green-100 text-sm">{createdUser.email}</p>
            </div>
          </div>
        </div>

        {/* Contenuto */}
        <div className="p-6 space-y-4">
          <p className="text-slate-300">
            L'utente è stato creato correttamente. Vuoi compilare il profilo ora per conto dell'utente?
          </p>

          {/* Info box */}
          <div className="bg-slate-700/50 rounded-xl p-4 border border-slate-600/50">
            <div className="flex items-start gap-3">
              <UserPlus className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-slate-400">
                <p>Il profilo verrà aperto in una nuova scheda. Potrai inserire tutti i dati del cliente (indirizzo, dati fiscali, referenti, ecc.).</p>
              </div>
            </div>
          </div>

          {/* Pulsanti */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleCloseSuccess}
              className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-xl transition-colors flex items-center justify-center gap-2 border border-slate-600"
            >
              <X className="w-4 h-4" />
              Chiudi
            </button>
            <button
              onClick={handleCompleteProfile}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white rounded-xl transition-colors flex items-center justify-center gap-2 font-medium shadow-lg shadow-blue-500/25"
            >
              <UserPlus className="w-4 h-4" />
              Compila Profilo
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Modalità embedded: renderizza solo il form (il container/header lo fornisce il parent)
  if (embedded) {
    return (
      <>
        <div className="p-6">
          {formContent}
        </div>
        {/* Popup successo anche in modalità embedded */}
        {successModal}
      </>
    );
  }

  // Modalità modal: overlay fisso
  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
        <div className="bg-slate-800 p-6 rounded-lg max-w-md w-full border border-slate-600 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-white">Crea Nuovo Utente</h3>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white text-xl font-bold"
            >
              ×
            </button>
          </div>
          {formContent}
        </div>
      </div>

      {/* Popup successo */}
      {successModal}
    </>
  );
};

export default UserCreateModal;
